/**
 * Component Name: Wallpaper
 *
 * Description:
 * The Wallpaper component renders a visually engaging full-width background image
 * with optional parallax scrolling. It supports customizable height and allows child
 * components to be embedded within its canvas for added flexibility.
 *
 * Usage:
 * <Wallpaper height="full" asset="path/to/image.jpg">
 *     <h1>Your Content Here</h1>
 * </Wallpaper>
 *
 * Props:
 * - `height` ('full' | 'standard', optional): Defines the height of the wallpaper.
 *   Defaults to 'standard' (600px on desktop, 300px on mobile).
 * - `asset` (string, required): Path to the background image.
 * - `children` (ReactNode, optional): Content to be displayed on top of the wallpaper.
 *
 * Example:
 * <Wallpaper height="full" asset="/assets/images/sample.jpg">
 *     <FPO message="For Placement Only" />
 * </Wallpaper>
 *
 * Notes:
 * - The parallax effect is applied by scaling the background image as the user scrolls.
 * - Use appropriate image dimensions for better performance and visual quality.
 *
 */

import React, { useEffect, useRef } from 'react';
import './Wallpaper.styles.scss';

interface WallpaperProps {
  height?: 'full' | 'standard';
  asset: string;
  disableScrollAnimation?: boolean;
  children?: React.ReactNode; // Allows passing child components
}

const Wallpaper: React.FC<WallpaperProps> = ({
  height = 'standard',
  asset,
  children,
  disableScrollAnimation,
}) => {
  const wallpaperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disableScrollAnimation) return;
    const handleScroll = () => {
      if (!wallpaperRef.current) return;

      const rect = wallpaperRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Calculate the scroll percentage (0 = top of the viewport, 1 = fully visible)
      const scrollPercentage = Math.max(
        0,
        Math.min(1, (windowHeight - rect.top) / (windowHeight + rect.height))
      );

      // Scale from 1 to 1.25 based on scroll percentage
      const scale = 1 + 0.25 * scrollPercentage;

      const image = wallpaperRef.current.querySelector(
        '.wallpaper-image'
      ) as HTMLElement;
      if (image) {
        image.style.transform = `scale(${scale})`;
      }
    };

    // Attach the scroll listener
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [disableScrollAnimation]);

  return (
    <div
      ref={wallpaperRef}
      className={`wallpaper-container ${height === 'full' ? 'height-full' : 'height-standard'}`}
    >
      <div
        className="wallpaper-image"
        style={{
          backgroundImage: `url(${asset})`,
        }}
      />
      {children && <div className="wallpaper-content">{children}</div>}{' '}
      {/* Child elements */}
    </div>
  );
};

export default Wallpaper;
